<script setup lang="ts">
/**
 * One plan on offer (the Billing page): its name, who it is for, its price, what it includes, and whatever the screen
 * lets a person do with it (the `action` slot). The plan the enterprise is on says so three ways (BG §22.3, colour
 * never carries meaning alone): the pill's words, `aria-current`, and the command cyan on its hairline.
 */
import ZhStatusPill from './ZhStatusPill.vue'

withDefaults(
    defineProps<{
        name: string
        /** Who the plan is for: "Growing Teams". */
        tagline?: string
        /** The price as it reads: "$199", "From $1,499", "Free". */
        price: string
        /** What the price is for: "a month", "a year". */
        per?: string
        /** The plan the enterprise is on. */
        current?: boolean
        /** What the pill says on the current plan. */
        currentLabel?: string
        /** What the plan includes, one line each. */
        features?: readonly string[]
        as?: string
    }>(),
    { current: false, currentLabel: 'Current plan', features: () => [], as: 'article' },
)
</script>

<template>
    <component :is="as" class="zh-plan-card" :data-current="current ? 'on' : 'off'" :aria-current="current ? 'true' : undefined">
        <header class="zh-plan-card__head">
            <h3 class="zh-plan-card__name">{{ name }}</h3>
            <ZhStatusPill v-if="current" tone="plan">{{ currentLabel }}</ZhStatusPill>
        </header>
        <p v-if="tagline" class="zh-plan-card__tagline">{{ tagline }}</p>
        <p class="zh-plan-card__price">
            <span class="zh-plan-card__amount">{{ price }}</span>
            <span v-if="per" class="zh-plan-card__per">{{ per }}</span>
        </p>
        <ul v-if="features.length > 0" class="zh-plan-card__features">
            <li v-for="feature in features" :key="feature">{{ feature }}</li>
        </ul>
        <div v-if="$slots['action']" class="zh-plan-card__action">
            <slot name="action" />
        </div>
    </component>
</template>

<style scoped>
.zh-plan-card {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
    padding: 1rem;
    border: 1px solid var(--border);
    background: var(--panel);
}

.zh-plan-card[data-current='on'] {
    border-color: var(--electric-cyan);
}

.zh-plan-card__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 1.5rem;
}

.zh-plan-card__name {
    margin: 0;
    color: var(--ink-1);
    font-size: 1rem;
    font-weight: 600;
}

.zh-plan-card__tagline {
    margin: 0;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-plan-card__price {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.375rem;
    margin: 0;
}

.zh-plan-card__amount {
    color: var(--ink-1);
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.1;
}

.zh-plan-card__per {
    color: var(--ink-2);
    font-size: 0.875rem;
}

.zh-plan-card__features {
    display: grid;
    gap: 0.25rem;
    margin: 0;
    padding: 0.75rem 0 0;
    border-top: 1px solid var(--hair);
    list-style: none;
    color: var(--ink-2);
    font-size: 0.875rem;
}

/* The action sits on the card's floor, so the buttons of a row of cards line up whatever each card lists. */
.zh-plan-card__action {
    margin-top: auto;
    padding-top: 0.25rem;
}
</style>
